import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, map, mergeMap, takeUntil } from 'rxjs/operators'

import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from '../../PartnerOptions/constants'

import {
  addRecipientError,
  addRecipientSuccess,
  getCandidatesError,
  getCandidatesSuccess,
  getGeneralSettingsError,
  getGeneralSettingsSuccess,
  getRecipientsError,
  getRecipientsSuccess,
  getTagsError,
  getTagsSuccess,
  removeRecipientsError,
  removeRecipientsSuccess,
  updateGeneralSettingsError,
  updateGeneralSettingsSuccess,
} from './actions'
import {
  ADD_RECIPIENT_ASYNC,
  CANDIDATES_PER_PAGE,
  GENERAL_SETTINGS_REQUEST_ASYNC,
  GET_CANDIDATES_ASYNC,
  GET_RECIPIENTS_ASYNC,
  GET_TAGS_ASYNC,
  RECIPIENTS_PER_PAGE,
  REMOVE_RECIPIENT_ASYNC,
  SET_RECIPIENTS_PAGE,
  UPDATE_GENERAL_SETTINGS_REQUEST,
} from './constants'
import { selectCandidatesData, selectRecipientList, selectTagsData } from './selectors'

const getGeneralSettingsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      GENERAL_SETTINGS_REQUEST_ASYNC.INITIATE,
      UPDATE_GENERAL_SETTINGS_REQUEST.SUCCESS
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.settings.simulator)
      return true
    }),
    mergeMap(() =>
      api.settings.getSimulatorSettings(state$.value.partnerOptions.selectedCompany?.value).pipe(
        map((response) => getGeneralSettingsSuccess(response)),
        catchError((error) => of(epicsErrorHandler(getGeneralSettingsError, error))),
        takeUntil(action$.pipe(ofType(GENERAL_SETTINGS_REQUEST_ASYNC.CANCEL)))
      )
    )
  )

const updateGeneralSettingsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_GENERAL_SETTINGS_REQUEST.INITIATE),
    mergeMap(() =>
      api.settings
        .updateSimulatorSettings(
          state$.value.partnerOptions.selectedCompany?.value,
          state$.value.form.SimulatorSettingsForm.values
        )
        .pipe(
          map((response) => updateGeneralSettingsSuccess(response)),
          catchError((error) => of(epicsErrorHandler(updateGeneralSettingsError, error, [400]))),
          takeUntil(action$.pipe(ofType(UPDATE_GENERAL_SETTINGS_REQUEST.CANCEL)))
        )
    )
  )

const getTagsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_TAGS_ASYNC.INITIATE, ADD_RECIPIENT_ASYNC.SUCCESS, REMOVE_RECIPIENT_ASYNC.SUCCESS),
    mergeMap((action) => {
      const notDirectRequest = action.type !== GET_TAGS_ASYNC.INITIATE
      const page = notDirectRequest ? 1 : action.payload.params.page
      const search = notDirectRequest ? '' : action.payload.params.search

      return api.mailboxes
        .getTags({
          companyId: state$.value.partnerOptions.selectedCompany?.value,
          allTags: false,
          page,
          search,
        })
        .pipe(
          map((response) => getTagsSuccess(response)),
          catchError((error) => of(epicsErrorHandler(getTagsError, error))),
          takeUntil(action$.pipe(ofType(GET_TAGS_ASYNC.CANCEL)))
        )
    })
  )

const getCandidatesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      GET_CANDIDATES_ASYNC.INITIATE,
      ADD_RECIPIENT_ASYNC.SUCCESS,
      REMOVE_RECIPIENT_ASYNC.SUCCESS
    ),
    mergeMap((action) => {
      const notDirectRequest = action.type !== GET_CANDIDATES_ASYNC.INITIATE
      const page = notDirectRequest ? 1 : action.payload.params.page
      const search = notDirectRequest ? '' : action.payload.params.search

      return api.settings
        .getCandidates(
          state$.value.partnerOptions.selectedCompany?.value,
          page,
          CANDIDATES_PER_PAGE,
          search
        )
        .pipe(
          mergeMap((response) => of(getCandidatesSuccess(response))),
          catchError((error) => of(epicsErrorHandler(getCandidatesError, error, [400]))),
          takeUntil(action$.pipe(ofType(GET_CANDIDATES_ASYNC.CANCEL)))
        )
    })
  )

const getRecipientsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_RECIPIENTS_ASYNC.INITIATE, ADD_RECIPIENT_ASYNC.SUCCESS, SET_RECIPIENTS_PAGE),
    mergeMap(() =>
      api.settings
        .getRecipients(
          state$.value.partnerOptions.selectedCompany?.value,
          selectRecipientList(state$.value).page,
          RECIPIENTS_PER_PAGE
        )
        .pipe(
          mergeMap((response) => of(getRecipientsSuccess(response))),
          catchError((error) => of(epicsErrorHandler(getRecipientsError, error, [400]))),
          takeUntil(action$.pipe(ofType(GET_RECIPIENTS_ASYNC.CANCEL)))
        )
    )
  )

const addRecipientEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(ADD_RECIPIENT_ASYNC.INITIATE),
    mergeMap(() =>
      api.settings
        .addRecipient(
          state$.value.partnerOptions.selectedCompany?.value,
          selectCandidatesData(state$.value).value.map(({ value, type }) => ({
            id: value,
            type,
          })),
          selectTagsData(state$.value).value
        )
        .pipe(
          mergeMap((response) => of(addRecipientSuccess(response))),
          catchError((error) => of(epicsErrorHandler(addRecipientError, error, [400]))),
          takeUntil(action$.pipe(ofType(ADD_RECIPIENT_ASYNC.CANCEL)))
        )
    )
  )

const removeRecipientsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(REMOVE_RECIPIENT_ASYNC.INITIATE),
    mergeMap(() => {
      const selectionData = selectRecipientList(state$.value).selectionData

      return api.settings
        .removeRecipients(
          state$.value.partnerOptions.selectedCompany?.value,
          selectionData.allItems
            ? {
                allItems: true,
                excludeIds: selectionData.selectionIds,
              }
            : {
                allItems: false,
                includeIds: selectionData.selectionIds,
                excludeIds: [],
              }
        )
        .pipe(
          mergeMap((response) => of(removeRecipientsSuccess(response))),
          catchError((error) => of(epicsErrorHandler(removeRecipientsError, error, [400]))),
          takeUntil(action$.pipe(ofType(REMOVE_RECIPIENT_ASYNC.CANCEL)))
        )
    })
  )

export default combineEpics(
  getGeneralSettingsEpic,
  updateGeneralSettingsEpic,
  getTagsEpic,
  getCandidatesEpic,
  getRecipientsEpic,
  addRecipientEpic,
  removeRecipientsEpic
)
